import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentResult() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, failed, cancelled
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = localStorage.getItem('checkout_session_id');
      const token = localStorage.getItem('token');
      
      // Check if this is a cancel redirect
      if (searchParams.get('cancelled') === 'true') {
        setStatus('cancelled');
        setMessage('Payment was cancelled.');
        localStorage.removeItem('checkout_session_id');
        return;
      }

      if (!sessionId || !token) {
        setStatus('failed');
        setMessage('Session not found. Please try subscribing again.');
        return;
      }

      try {
        const response = await axios.get(
          `${API}/api/subscription/verify/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          setStatus('success');
          setMessage('Your subscription is now active!');
          
          // Update stored user with new subscription
          const userResponse = await axios.get(`${API}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          localStorage.setItem('user', JSON.stringify(userResponse.data));
        } else {
          setStatus('failed');
          setMessage(response.data.message || 'Payment verification failed.');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('failed');
        setMessage('Error verifying payment. Please contact support.');
      } finally {
        localStorage.removeItem('checkout_session_id');
      }
    };

    verifyPayment();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin text-primary" />
              <CardTitle>Verifying Payment...</CardTitle>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <CardTitle className="text-green-500">Payment Successful!</CardTitle>
            </>
          )}
          {status === 'failed' && (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
              <CardTitle className="text-red-500">Payment Failed</CardTitle>
            </>
          )}
          {status === 'cancelled' && (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
              <CardTitle className="text-yellow-500">Payment Cancelled</CardTitle>
            </>
          )}
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">{message}</p>
          
          {status === 'success' && (
            <div className="space-y-2">
              <Button onClick={() => navigate('/tracker')} className="w-full">
                Go to Live Tracker
              </Button>
            </div>
          )}
          
          {(status === 'failed' || status === 'cancelled') && (
            <div className="space-y-2">
              <Button onClick={() => navigate('/subscribe')} className="w-full">
                Try Again
              </Button>
              <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                Go Home
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
